import type { FormId } from "@@/shared/form/form-and-field-id";
import type { UseMutationResult } from "@tanstack/react-query";
import type { InferTypeFromSchema } from "@towni/common";
import { generateId } from "@towni/common";
import { useMemo, useState } from "react";
import { type SafeParseReturnType, type ZodSchema } from "zod";
import { FormIdProvider } from "./form-id.context";
import type { Form3Components } from "./form3-components";
import type { Form3ErrorMessage } from "./form3.error-message";
import type { Form3Actions } from "./form3.form-actions";
import type { UseForm3Params } from "./form3.use-form";
import { useForm3 } from "./form3.use-form";

// const shallowCompareArray = <T,>(a: T[], b: T[]): boolean => {
//     if (a.length !== b.length) return false;
//     for (let i = 0; i < a.length; i++) {
//         if (a[i] !== b[i]) return false;
//     }
//     return true;
// };
// // eslint-disable-next-line @typescript-eslint/no-explicit-any
// const fieldValueCache = new Map<string, any>();
type InitializeFormParams<
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
    _FieldId,
> = {
    readonly formId?: FormId;
    readonly schema: FormSchema;
    readonly initialData: Partial<FormData>;
    readonly clearOnUnmount?: boolean;
    readonly mutation?: UseMutationResult<unknown, unknown, FormData>;
};

type Form<
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
    FieldId extends string,
> = {
    _id: FormId;
    data: Partial<FormData>;
    initialData: Partial<FormData>;
    schema: FormSchema;
    touched: Record<string, boolean>;
    dirty: Record<string, boolean>;
    initialHash: string;
    currentHash: string;
    hasChanges: boolean;
    clearOnUnmount: boolean;
    mutation:
        | InitializeFormParams<FormSchema, FormData, FieldId>["mutation"]
        | undefined;
    parse: () => FormData;
    safeParse: () => SafeParseReturnType<Partial<FormData>, FormData>;
    setHash: (hash: string) => void;
    parseSafely: () => FormData | undefined;
    calculateHash: (data: FormData) => string;
    calculateFieldHash: (fieldId: FieldId) => string;
    isFieldChanged: (fieldId: FieldId) => boolean;
    setData: (formData: FormData) => void;
    setDirty: (fieldId: FieldId, value: boolean) => void;
    setTouched: (fieldId: FieldId, value: boolean) => void;
    setFormField: <const FieldValue>(
        fieldId: FieldId,
        value:
            | FieldValue
            | null
            | undefined
            | ((
                  currentValue: FieldValue | null | undefined,
              ) => FieldValue | null | undefined),
    ) => void;
    getFieldSchema: (fieldId: FieldId) => ZodSchema<unknown> | undefined;
    getFieldValue: <const FieldValue>(
        fieldId: FieldId,
    ) => FieldValue | undefined | null;
    validate: () => Form3ErrorMessage[];
    validateField: <const FieldValue>(
        fieldId: FieldId,
        options?: {
            preProcessValue?: (
                value: FieldValue | undefined | null,
            ) => FieldValue | undefined;
            customErrorMessage?: Form3ErrorMessage;
        },
    ) => Form3ErrorMessage[];
    resetToInitialState: (formData?: FormData) => void;
};

const _Form = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
    FieldId extends string,
>(
    props: UseForm3Params<FormSchema, FormData, FieldId> & {
        readonly children: (params: {
            form: Form<FormSchema, FormData, FieldId>;
            actions: Form3Actions<FormData>;
            FormComponents: Form3Components<FormSchema, FormData>;
        }) => React.ReactNode;
    },
) => {
    const { formId, children, ...useFormParams } = props;
    // Set to automatically clear the form when the component unmounts
    // if the formId is not provided (and hence is created by our form)
    // or if the initializeIfNotExists.clearOnUnmount is true
    const clearOnUnmount =
        props.initializeIfNotExists?.clearOnUnmount ?? !props.formId;
    const params = useMemo(
        (): UseForm3Params<FormSchema, FormData, FieldId> => ({
            ...useFormParams,
            formId,
            initializeIfNotExists: props.initializeIfNotExists
                ? {
                      ...props.initializeIfNotExists,
                      clearOnUnmount,
                      formId,
                  }
                : undefined,
        }),
        [useFormParams, formId, props.initializeIfNotExists, clearOnUnmount],
    );

    // Get the form and actions from the useForm hook
    const [form, actions, FormComponents] = useForm3<
        FormSchema,
        FormData,
        FieldId
    >(params);

    // Render the children with the form and actions
    // Wrap it in a FormIdProvider to provide the formId
    // to form components and hooks further down the tree
    return (
        <FormIdProvider key={form._id} formId={form._id}>
            {children({ form, actions, FormComponents })}
        </FormIdProvider>
    );
};

const Form = <
    FormSchema extends ZodSchema,
    FormData extends InferTypeFromSchema<FormSchema>,
    FieldId extends string,
>(
    props: UseForm3Params<FormSchema, FormData, FieldId> & {
        readonly children: (params: {
            form: Form<FormSchema, FormData, FieldId>;
            actions: Form3Actions<FormData>;
            FormComponents: Form3Components<FormSchema, FormData>;
        }) => React.ReactNode;
    },
) => {
    const { formId: _formId, ...useFormParams } = props;
    const [formId] = useState(
        () => _formId ?? generateId<FormId>({ prefix: "form__" }),
    );

    return (
        <FormIdProvider formId={formId}>
            <_Form {...useFormParams} formId={formId} />
        </FormIdProvider>
    );
};

export { Form as Form3 };
export type { InitializeFormParams as InitializeForm3Params, Form as TForm3 };
